import { useCallback, useEffect, useState } from "react";
import { Users, Send } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Input } from "./ui/input";
import { Button } from "./ui/button";

export const JoinGroupCard = () => {
  const { t } = useAuth();
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);
  const [mine, setMine] = useState([]);
  const load = useCallback(() => api.get("/join-requests/mine").then((r) => setMine(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/join-requests", { group_name: name });
      toast.success(t("join.sent"));
      setName("");
      load();
    } catch (err) {
      toast.error(err.friendly);
    }
    setLoading(false);
  };
  return (
    <div className="app-card p-6 space-y-3 max-w-lg" data-testid="join-group-card">
      <h3 className="font-heading text-lg font-bold flex items-center gap-2"><Users className="h-5 w-5 accent" /> {t("join.card_title")}</h3>
      <p className="text-xs t-secondary">{t("join.card_desc")}</p>
      <form onSubmit={submit} className="flex gap-2">
        <Input value={name} onChange={(e) => setName(e.target.value)} required placeholder={t("join.name_placeholder")} className="bg-transparent border-app" data-testid="join-group-name-input" />
        <Button type="submit" disabled={loading || !name.trim()} className="brand-bg text-white shrink-0" data-testid="join-group-submit">
          <Send className="h-4 w-4 md:mr-1" /><span className="hidden md:inline">{t("join.send")}</span>
        </Button>
      </form>
      {mine.length > 0 && (
        <div className="space-y-1.5 pt-1" data-testid="join-my-requests">
          <p className="text-xs uppercase tracking-wider t-secondary">{t("join.my_requests")}</p>
          {mine.map((r) => (
            <div key={r.id} className="flex items-center justify-between gap-2 text-sm" data-testid={`join-my-request-${r.id}`}>
              <span className="truncate">{r.group_name}</span>
              <span className={`text-xs px-2 py-0.5 rounded-full shrink-0 ${r.status === "ACCEPTED" ? "bg-emerald-500/15 text-emerald-400" : r.status === "DECLINED" ? "bg-rose-500/15 text-rose-400" : "bg-amber-500/15 text-amber-400"}`} data-testid={`join-my-status-${r.id}`}>
                {t(`join.status_${r.status.toLowerCase()}`)}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export const JoinRequestsAdminCard = () => {
  const { t } = useAuth();
  const [reqs, setReqs] = useState([]);
  const load = useCallback(() => api.get("/join-requests").then((r) => setReqs(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  const decide = async (r, action) => {
    try {
      await api.post(`/join-requests/${r.id}/${action}`);
      toast.success(t(action === "accept" ? "join.accepted_toast" : "join.declined_toast"));
      load();
    } catch (err) {
      toast.error(err.friendly);
    }
  };
  if (!reqs.length) return null;
  return (
    <div className="app-card p-6 space-y-3 max-w-lg" data-testid="join-requests-admin-card">
      <h3 className="font-heading text-lg font-bold flex items-center gap-2">
        <Users className="h-5 w-5 accent" /> {t("join.admin_title")}
        <span className="text-xs px-2 py-0.5 rounded-full brand-bg text-white" data-testid="join-requests-count">{reqs.length}</span>
      </h3>
      <div className="space-y-2">
        {reqs.map((r) => (
          <div key={r.id} className="flex items-center gap-2" data-testid={`join-request-${r.id}`}>
            <span className="flex-1 text-sm truncate">{r.prenom} {r.nom}</span>
            <Button size="sm" className="brand-bg text-white" onClick={() => decide(r, "accept")} data-testid={`join-accept-${r.id}`}>{t("join.accept")}</Button>
            <Button size="sm" variant="outline" className="border-app" onClick={() => decide(r, "decline")} data-testid={`join-decline-${r.id}`}>{t("join.decline")}</Button>
          </div>
        ))}
      </div>
    </div>
  );
};
